import { Link } from "react-router-dom";
import { MapPin, Sparkles, ShieldCheck, HeartHandshake, Users2, Calendar, Rocket } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";

const heroImg = "https://images.unsplash.com/photo-1637430308606-86576d8fef3c?w=1400&q=80";
const yoga = "https://images.unsplash.com/photo-1761971975962-9cc397e2ba2a?w=800&q=80";
const coach = "https://images.unsplash.com/photo-1750698545009-679820502908?w=800&q=80";

export default function Home() {
  const { user } = useAuth();
  const { lang, t } = useI18n();
  return (
    <div>
      <Navbar />

      {/* Hero */}
      <section className="sc-hero relative">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-center sc-fade-in">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/20">
              <Sparkles className="w-3.5 h-3.5" /> Nouveau · Sans abonnement mensuel
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900">
              {lang === "en" ? <>Free time slots in gyms,<br/><span className="text-emerald-600">turned into opportunities.</span></> : <>Les créneaux libres des salles,<br/><span className="text-emerald-600">transformés en opportunités.</span></>}
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-2xl">
              {lang === "en" ? <>TymCoach connects <b>coaches</b> looking for hourly venues, <b>gyms</b> that want to monetise their idle hours and <b>individuals</b> who prefer paying per session, with no commitment. Motivation, without the anxiety.</>
                : <>TymCoach connecte les <b>coachs</b> qui cherchent des salles à l'heure, les <b>salles</b> qui veulent rentabiliser leurs contingences et les <b>particuliers</b> qui préfèrent réserver à la séance, sans engagement. La motivation, sans l'anxiété.</>}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link data-testid="hero-cta-map" to="/carte" className="sc-btn sc-btn-primary">
                <MapPin className="w-4 h-4" /> {lang === "en" ? "Explore the map" : "Explorer la carte"}
              </Link>
              {(!user || user === false) && (
                <Link data-testid="hero-cta-register" to="/inscription" className="sc-btn sc-btn-emerald">
                  <Rocket className="w-4 h-4" /> Rejoindre TymCoach
                </Link>
              )}
            </div>

            <div className="mt-10 grid grid-cols-3 max-w-lg gap-6">
              <Stat label="Salles partenaires" value="120+" />
              <Stat label="Coachs certifiés" value="450+" />
              <Stat label="Villes couvertes" value="24" />
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-slate-200">
              <img src={heroImg} alt="Salle de sport moderne" className="w-full h-[420px] object-cover" />
              <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur rounded-2xl p-4 border border-slate-200">
                <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider">À la une</div>
                <div className="mt-1 font-bold text-slate-900">Studio Bastille Yoga · Paris 11</div>
                <div className="text-sm text-slate-600">Créneau libre mercredi 10h · 25€/h</div>
              </div>
            </div>
            <img src={coach} alt="Coach" className="hidden lg:block absolute -left-10 -bottom-10 w-40 h-40 object-cover rounded-2xl border-4 border-white shadow-xl" />
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          <RoleCard
            color="emerald"
            icon={<HeartHandshake className="w-5 h-5" />}
            title="Particulier"
            baseline="Bougez sans engagement mensuel."
            points={["Réservez à la séance", "Coachs vérifiés", "Annulation flexible"]}
          />
          <RoleCard
            color="orange"
            icon={<Users2 className="w-5 h-5" />}
            title="Coach"
            baseline="Découvrez de nouvelles salles sans loyer fixe."
            points={["Salles à l'heure", "Réservations pour vos clients", "Notoriété locale"]}
          />
          <RoleCard
            color="indigo"
            icon={<ShieldCheck className="w-5 h-5" />}
            title="Responsable de Salle"
            baseline="Rentabilisez vos créneaux vacants."
            points={["Publiez en 3 clics", "Validez chaque coach", "Notifications instantanées"]}
          />
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <img src={yoga} alt="Studio yoga" className="rounded-3xl shadow-xl border border-slate-200" />
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="text-xs uppercase font-semibold tracking-wider text-slate-500">Le fonctionnement</div>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{t("Une contingence devient une opportunité.")}</h2>
            <ul className="mt-6 space-y-4">
              <Step n={1} title="La salle publie ses créneaux libres" body="Date, heure, équipements inclus, tarif à l'heure." />
              <Step n={2} title="Le coach propose une session à un client" body="Il choisit la salle, le créneau et envoie la demande." />
              <Step n={3} title="La salle valide (ou refuse)" body="Selon son niveau de coopération. Notification instantanée." />
              <Step n={4} title="Le client bouge, motivé, sans engagement" body="Séance à la carte, échanges via messagerie intégrée." />
            </ul>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="rounded-3xl bg-slate-900 text-white p-10 md:p-16 relative overflow-hidden sc-grain">
          <div className="relative z-10 max-w-2xl">
            <Calendar className="w-8 h-8 text-emerald-400" />
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t("Prêt à essayer sans engagement ?")}</h2>
            <p className="mt-3 text-slate-300">{t("Créez votre compte gratuitement et découvrez la carte des salles et coachs près de chez vous.")}</p>
            <div className="mt-6 flex gap-3 flex-wrap">
              <Link to="/inscription" data-testid="cta-final-register" className="sc-btn sc-btn-emerald">Créer un compte</Link>
              <Link to="/carte" data-testid="cta-final-map" className="sc-btn sc-btn-outline text-white border-white/20 hover:bg-white/10">Voir la carte</Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-2xl font-extrabold text-slate-900">{value}</div>
      <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{label}</div>
    </div>
  );
}
function Step({ n, title, body }) {
  return (
    <li className="flex gap-4">
      <div className="w-9 h-9 rounded-full bg-slate-900 text-white grid place-items-center font-bold shrink-0">{n}</div>
      <div>
        <div className="font-semibold text-slate-900">{title}</div>
        <div className="text-sm text-slate-600">{body}</div>
      </div>
    </li>
  );
}
function RoleCard({ color, icon, title, baseline, points }) {
  const map = {
    emerald: { border: "border-emerald-500/20", tag: "bg-emerald-500/10 text-emerald-700" },
    orange:  { border: "border-orange-500/20",  tag: "bg-orange-500/10 text-orange-700" },
    indigo:  { border: "border-indigo-500/20",  tag: "bg-indigo-500/10 text-indigo-700" },
  }[color];
  return (
    <div className={`sc-card ${map.border}`}>
      <div className={`inline-flex items-center gap-2 text-xs font-semibold px-2.5 py-1 rounded-full ${map.tag}`}>{icon}{title}</div>
      <div className="mt-3 text-xl font-bold text-slate-900">{baseline}</div>
      <ul className="mt-4 space-y-2 text-sm text-slate-700">
        {points.map((p) => <li key={p} className="flex gap-2"><span className="text-emerald-600">✓</span>{p}</li>)}
      </ul>
    </div>
  );
}
